export const BACKEND_URL  = import.meta.env.VITE_API_URL;

const USERS_ENDPOINT = 'users'
const MANAGED_CONTENT = 'managed_content'
const FLEETS = 'fleets'
const VEHICLES = 'vehicles'
const LOCATIONS = 'locations'
const ROUTE_PLANS = 'route_plans'
const USER_SETTINGS = 'user_settings'
const VEHICLE_IMAGES = 'vehicle_images'
const NOTIFICATIONS = 'notifications'
const USERS_NOTIFICATIONS = 'users_notifications'
const ASSET_BOOKINGS = 'asset_bookings'
const VEHICLES_VEHICLE_IMAGES = 'vehicles_vehicle_images'
const USER_INVITES = 'user_invites'

const ACTIONS = 'rpc/'

const makeAction = <T extends string>(action: T): `rpc/${T}` => {
  return `${ACTIONS}${action}` as `rpc/${T}`
}

/**
 * Creates a new user from the auth token.
 *
 * @returns returns the user object if successfully created
 * @throws will error if the user already exists
 */
const ACTION_CREATE_USER = makeAction('create_user')

/**
 * Checks if a user from the auth token exists.
 * @returns returns the users UUID if they do exist.
 * @throws will error if the user does not exist
 */
const ACTION_VERIFY_USER = makeAction('current_user_id')

/**
 * Creates a new fleet and assigns it to the user from the auth token.
 *
 * @returns returns the user object if successfully created
 * @throws will error if the user is already assigned to a fleet, the users token is invalid/expired
 * TODO: will also throw if the user has the incorrect role in keycloak
 * @body expected to at least have {'display_name': "[fleets display name]"} in the body
 */
const ACTION_CREATE_FLEET = makeAction('create_fleet')

/**
 * Checks if a user from the auth token is assigned to a fleet.
 * @returns returns the fleets UUID if they do exist.
 * @throws will error if the fleet or the user does not exist, the users token is invalid/expired
 */
const ACTION_VERIFY_FLEET = makeAction('current_fleet_id')

/**
 * Creates a new invite for a user.
 * @returns returns the invite object if successfully created
 * @throws will error if the user is already assigned to a fleet, the users token is invalid/expired
 */
const ACTION_CREATE_INVITE = makeAction('create_invite')

/**
 * Accepts an invite for a user.
 * @returns returns the user object if successfully created
 * @throws will error if the user is already assigned to a fleet, the users token is invalid/expired
 */
const ACTION_ACCEPT_INVITE = makeAction('accept_invite')

export const VALID_ENDPOINTS = [
  USERS_ENDPOINT,
  MANAGED_CONTENT,
  FLEETS,
  VEHICLES,
  LOCATIONS,
  ROUTE_PLANS,
  USER_SETTINGS,
  VEHICLE_IMAGES,
  NOTIFICATIONS,
  USERS_NOTIFICATIONS,
  ASSET_BOOKINGS,
  VEHICLES_VEHICLE_IMAGES,
  USER_INVITES,
  ACTION_VERIFY_USER,
  ACTION_CREATE_USER,
  ACTION_VERIFY_FLEET,
  ACTION_CREATE_FLEET,
  ACTION_CREATE_INVITE,
  ACTION_ACCEPT_INVITE,
] as const

export type ValidEndpoints = (typeof VALID_ENDPOINTS)[number]

export function isValidEndpoint(value: string): value is ValidEndpoints {
  return VALID_ENDPOINTS.includes(value as ValidEndpoints)
}

export function assertValidEndpoint(value: string): ValidEndpoints {
  if (!isValidEndpoint(value)) {
    throw new Error(`Invalid API endpoint: ${value}`)
  }
  return value
}

export type Query = Record<
  string,
  string | number | boolean | string[] | number[] | boolean[] | null | undefined | object
>
